import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { combineLatest, Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';
import { DialogService, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { DBService } from 'src/app/core/backend-adapter/db.service';
import { SocketService } from 'src/app/core/backend-adapter/socket.service';
import { SessionService } from 'src/app/core/backend-adapter/session.service';
import { NotifyService } from 'src/app/core/layouts/notifications/notify/notify.service';
import { GbpQaAddLocationComponent } from 'src/app/gbp-management/gbp-tab-qa/components/gbp-qa-add-location/gbp-qa-add-location.component';
import { AbpLocationsFilterService } from 'src/app/abp-management/services/abp-locations-filter.service';
import { generateJWTToken } from 'src/app/core/helpers/jwt-token.utils';

@Component({
  selector: 'app-abp-post-locations',
  templateUrl: './abp-post-locations.component.html',
  styleUrls: ['./abp-post-locations.component.scss'],
})
export class AbpPostLocationsComponent implements OnInit, OnDestroy {
  tableData: any[] = [];
  addLocation = false;
  isPagination = false;
  bulkOwner = false;
  id: number;
  offset = 0;
  pageSize = 20;
  paginatorRows = 20;
  paginatorLength = 0;
  pendingPaginatorLength = 0;
  loading: boolean;
  whereClaus: any[] = [];
  ownerName: string = '';
  /** Location IDs from details API (UUIDs when filtered). */
  locationIds: (string | number)[] = [];
  isAllLocations: boolean = false;
  keyword: string = '';
  /** Full list from filters API for client-side pagination. */
  private filteredLocationsList: any[] = [];
  private unsubscribe$ = new Subject<void>();
  /** Selected account id (same as listings: from getSelectedAccount$()). */
  private currentAccountId: number;
  /** Current user login id (same as listings: from getCurrentUser$()). */
  private userLoginId: number;
  /** creative_id for fetching status from GET /apple/showcases?creative_id=... */
  private creativeId: string | number | null = null;
  /** Status per location_id from showcases API (for status column). */
  private statusFromShowcases: Record<string, string> = {};
  /** In-flight filters API request so init and lazy load do not fetch the same data twice. */
  private filtersLoadPromise: Promise<void> | null = null;

  private readonly JWT_SECRET = '2c4c4d06f0b35b921039d9df2725205d981ac00bf196eb71b02ce7571432e30a';
  private readonly SHOWCASES_API_BASE = 'https://io.renderseo.com/apple/showcases';

  /**
   * Logs the current paginator total for debugging (which code path last updated it).
   * Args: `context` — short label for the assignment site (e.g. method name).
   * Returns: `void`.
   */
  private logPaginatorLength(context: string): void {
    console.log('[AbpPostLocations] paginatorLength', {
      context,
      paginatorLength: this.paginatorLength,
      pendingPaginatorLength: this.pendingPaginatorLength,
    });
  }

  constructor(
    public dynamicDialogConfig: DynamicDialogConfig,
    public dialogService: DialogService,
    private dbService: DBService,
    private notifyService: NotifyService,
    private socketService: SocketService,
    private sessionService: SessionService,
    private locationsFilterService: AbpLocationsFilterService,
    private http: HttpClient,
    private cdr: ChangeDetectorRef,
  ) {}

  /**
   * Initializes dialog state and starts the first location load for the current mode.
   * Keeps the selected account and user IDs synchronized for downstream API calls, and only triggers
   * the filters-based loader once both identities are available when loading filtered/all locations.
   * Args: none.
   * Returns: `void`.
   */
  ngOnInit(): void {
    // Keep account and user in sync for use in API calls
    this.sessionService.getSelectedAccount$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((account) => {
        this.currentAccountId = account?._id;
      });
    this.sessionService.getCurrentUser$()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((user) => {
        this.userLoginId = user?.login?._id;
      });
    this.tableData = this.dynamicDialogConfig?.data?.locations;
    this.isPagination = Boolean(this.dynamicDialogConfig?.data?.isPagination);
    this.bulkOwner = Boolean(this.dynamicDialogConfig?.data?.bulkOwner);
    this.id = this.dynamicDialogConfig?.data?.id;
    this.ownerName = this.dynamicDialogConfig?.data?.owner || '';
    this.locationIds = this.dynamicDialogConfig?.data?.locationIds || [];
    this.isAllLocations = Boolean(this.dynamicDialogConfig?.data?.isAllLocations);
    this.creativeId = this.dynamicDialogConfig?.data?.creativeId ?? this.dynamicDialogConfig?.data?.showcaseData?.showcase_creative_id ?? null;

    // Filters API needs account + user; wait for both before first load so data shows without clicking pagination
    if (this.isAllLocations || this.locationIds.length > 0) {
      combineLatest([
        this.sessionService.getSelectedAccount$(),
        this.sessionService.getCurrentUser$(),
      ])
        .pipe(
          filter(([account, user]) => account?._id != null && user?.login?._id != null),
          take(1),
          takeUntil(this.unsubscribe$),
        )
        .subscribe(([account, user]) => {
          this.currentAccountId = account._id;
          this.userLoginId = user.login._id;
          this.ensureFiltersLocationsLoaded();
        });
    } else if (this.id) {
      console.log("in else")
      this.getAllLocations();
    }
  }

  private loadBulkOwnerLocations() {
    this.loading = true;
    this.dbService.QuestionGMB.loadObjects({
      offset: this.offset,
      limit: this.pageSize,
      where: `bulkOwner_id = ${this.id}`,
    })
      .then(
        (repl) => {
          if (repl.collection?.length == 0) {
            this.notifyService.error('There are no locations available');
            this.tableData = [];
            this.paginatorLength = 0;
          } else {
            this.tableData = repl.collection;
            this.paginatorLength = repl.totalEntries;
          }
          this.logPaginatorLength('loadBulkOwnerLocations');
        },
        (err: Error) => {
          this.tableData = [];
          console.warn('Error loading Locations: %j', err);
        }
      )
      .finally(() => {
        this.loading = false;
      });
  }

  onPageChange(event) {
    this.offset = event.first;
    this.pageSize = event.rows ?? this.paginatorRows;
    if (this.isAllLocations || this.locationIds.length > 0) {
      this.applyFilteredLocationsPage();
    } else {
      this.getAllLocations();
    }
  }

  /** Trigger first-page update after load so table and paginator refresh without user click. */
  private triggerFirstPageAfterLoad(): void {
    setTimeout(() => {
      this.onPageChange({ first: 0, rows: this.paginatorRows });
      this.cdr.detectChanges();
    }, 0);
  }

  private loadAllAccountLocations(): void {
    this.loading = true;
    // Fetch all locations for the account
    this.dbService.Location.loadObjects({
      offset: this.offset,
      limit: this.pageSize,
      where: []
    })
    .then(
      (repl) => {
        if (repl.collection?.length === 0) {
          this.notifyService.error('There are no locations available');
          this.tableData = [];
          this.paginatorLength = 0;
        } else {
          // Map location data to include storecode, businessname, and status
          this.tableData = repl.collection.map((loc: any) => ({
            _id: loc._id,
            storeCode: loc.storeCode || loc.storecode || '',
            businessName: loc.businessName || loc.business_name || '',
            status: this.getStatusDisplay(loc),
            ...loc
          }));
          this.paginatorLength = repl.totalEntries;
        }
        this.logPaginatorLength('loadAllAccountLocations');
        this.loading = false;
      },
      (err: Error) => {
        this.tableData = [];
        this.paginatorLength = 0;
        this.logPaginatorLength('loadAllAccountLocations(error)');
        console.warn('Error loading all locations: %j', err);
        this.notifyService.error('Failed to load locations');
        this.loading = false;
      }
    );
  }

  /**
   * Starts the filters-based location load only when no identical request is already in flight.
   * This prevents the initial `ngOnInit` flow and PrimeNG's lazy-load event from racing each other.
   * Returns: `void`. The active promise is tracked internally and cleared when the request finishes.
   */
  private ensureFiltersLocationsLoaded(): void {
    console.log("in ensureFiltersLocationsLoaded")
    if (this.filtersLoadPromise) {
      return;
    }

    const loadPromise = this.isAllLocations
      ? this.loadAllLocationsFromFiltersApi()
      : this.loadLocationsFromFiltersApi();
    console.log("loadPromise", loadPromise);
    this.filtersLoadPromise = loadPromise.finally(() => {
      this.filtersLoadPromise = null;
    });
  }

  /** Map /access/filters/locations API item to table row (storecode, business_name, location_id, etc.). */
  private mapFiltersApiLocation(loc: any): any {
    return {
      _id: loc._id ?? loc.id ?? loc.location_id,
      storeCode: loc.storeCode ?? loc.storecode ?? '',
      businessName: loc.businessName ?? loc.business_name ?? '',
      status: loc.status ?? '-',
      location_id: loc.location_id ?? loc.locationId ?? '',
      city: loc.city ?? '',
      state: loc.state ?? '',
      ...loc
    };
  }

  /** Parse API response (array or { locations: [] }). */
  private parseFiltersApiResponse(res: any): any[] {
    if (Array.isArray(res)) {
      return res;
    }
    if (res?.locations && Array.isArray(res.locations)) {
      return res.locations;
    }
    return [];
  }

  /**
   * Load all locations from GET /access/filters/locations (no params).
   * Keeps the table in loading state until the base locations and any showcase-status merge are both complete.
   * Delays publishing the paginator total until the table rows are ready, so both update together.
   * Returns: A promise that resolves after the current page has been applied and change detection has run.
   */
  private async loadAllLocationsFromFiltersApi(): Promise<void> {
    const accountId = this.currentAccountId;
    const userId = this.userLoginId;
    if (!accountId || !userId) {
      this.notifyService.error('Unable to load locations: session required');
      this.tableData = [];
      this.paginatorLength = 0;
      this.pendingPaginatorLength = 0;
      this.logPaginatorLength('loadAllLocationsFromFiltersApi(no session)');
      return;
    }

    this.loading = true;

    try {
      const res = await this.locationsFilterService.getFilteredLocations(accountId, userId, {});
      const items = this.parseFiltersApiResponse(res);
      this.filteredLocationsList = items.map((loc: any) => this.mapFiltersApiLocation(loc));
      this.pendingPaginatorLength = this.filteredLocationsList.length;
      await this.mergeStatusFromShowcasesThenApplyPage();
      this.cdr.detectChanges();
    } catch (err) {
      console.warn('Error loading locations from filters API:', err);
      this.notifyService.error('Failed to load locations');
      this.tableData = [];
      this.paginatorLength = 0;
      this.pendingPaginatorLength = 0;
      this.filteredLocationsList = [];
      this.logPaginatorLength('loadAllLocationsFromFiltersApi(catch)');
      this.cdr.detectChanges();
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Load locations from GET /access/filters/locations?location_id=uuid1,uuid2.
   * Only requests the given IDs; if API returns more, we filter client-side to show only those.
   * Keeps the table in loading state until the locations are filtered, statuses are merged, and the page is rendered.
   * Delays publishing the paginator total until the rendered page is ready so the count does not appear early.
   * Returns: A promise that resolves after the filtered page is ready for display.
   */
  private async loadLocationsFromFiltersApi(): Promise<void> {
    if (this.locationIds.length === 0) {
      return;
    }

    const accountId = this.currentAccountId;
    const userId = this.userLoginId;
    if (!accountId || !userId) {
      this.notifyService.error('Unable to load locations: session required');
      this.tableData = [];
      this.paginatorLength = 0;
      this.pendingPaginatorLength = 0;
      this.logPaginatorLength('loadLocationsFromFiltersApi(no session)');
      return;
    }

    this.loading = true;
    // Exact format: location_id=id1,id2 (comma-separated, no spaces)
    const locationIdParam = this.locationIds.map(id => String(id).trim()).join(',');
    const queryParams = { location_id: locationIdParam };

    try {
      const res = await this.locationsFilterService.getFilteredLocations(accountId, userId, queryParams);
      console.log("res from loadLocationsFromFiltersApi : ", res);
      const items = this.parseFiltersApiResponse(res);
      const mapped = items.map((loc: any) => this.mapFiltersApiLocation(loc));
      // If API ignored location_id and returned all, filter client-side to only requested IDs
      const requestedIds = new Set(this.locationIds.map(id => String(id).trim().toLowerCase()));
      this.filteredLocationsList = mapped.filter((loc: any) => {
        const locId = String(loc.location_id || loc.locationId || '').trim().toLowerCase();
        return locId && requestedIds.has(locId);
      });
      this.pendingPaginatorLength = this.filteredLocationsList.length;
      await this.mergeStatusFromShowcasesThenApplyPage();
      this.cdr.detectChanges();
    } catch (err) {
      console.warn('Error loading locations from filters API:', err);
      this.notifyService.error('Failed to load locations');
      this.tableData = [];
      this.paginatorLength = 0;
      this.pendingPaginatorLength = 0;
      this.filteredLocationsList = [];
      this.logPaginatorLength('loadLocationsFromFiltersApi(catch)');
      this.cdr.detectChanges();
    } finally {
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  /**
   * Applies the current client-side page and publishes the pending paginator total with the row data.
   * If there are no filtered results, both the table rows and paginator count are cleared together.
   * Returns: `void`.
   */
  private applyFilteredLocationsPage(): void {
    if (this.filteredLocationsList.length === 0) {
      this.tableData = [];
      this.paginatorLength = this.pendingPaginatorLength;
      this.logPaginatorLength('applyFilteredLocationsPage(empty list)');
      this.cdr.detectChanges();
      return;
    }
    const start = this.offset;
    const pageSize = this.pageSize > 0 ? this.pageSize : this.paginatorRows;
    const end = start + pageSize;
    this.tableData = this.filteredLocationsList.slice(start, end);
    this.paginatorLength = this.pendingPaginatorLength;
    this.logPaginatorLength('applyFilteredLocationsPage');
    this.cdr.detectChanges();
  }

  /**
   * If creativeId is set, fetch status from GET /apple/showcases?creative_id=... and merge into
   * filteredLocationsList for the status column; then apply page.
   * Ensures pagination refresh happens before the caller clears the loading state.
   * Returns: A promise that resolves after the current page is ready to render.
   */
  private async mergeStatusFromShowcasesThenApplyPage(): Promise<void> {
    if (!this.creativeId) {
      this.applyFilteredLocationsPage();
      this.triggerFirstPageAfterLoad();
      return;
    }

    await this.fetchStatusFromShowcasesApi();
  }

  /**
   * Call GET https://io.renderseo.com/apple/showcases?creative_id=... with auth,
   * parse response to extract status per location, merge into filteredLocationsList, then apply page.
   * Resolves only after the status merge and first-page refresh have finished.
   * Returns: A promise that always resolves after the table data has been updated, even on request failure.
   */
  private fetchStatusFromShowcasesApi(): Promise<void> {
    const accountId = this.currentAccountId;
    const userId = this.userLoginId;
    if (!accountId || !userId) {
      this.applyFilteredLocationsPage();
      this.triggerFirstPageAfterLoad();
      return Promise.resolve();
    }

    const payloadObj = {
      iss: 'https://platform.renderseo.com',
      sub: userId,
      account_id: accountId,
      iat: new Date().getTime(),
      exp: new Date().getTime() + 3 * 6000,
    };
    const jwtToken = generateJWTToken(payloadObj, this.JWT_SECRET);
    const headers = new HttpHeaders()
      .set('Authorization', jwtToken)
      .set('Content-Type', 'application/json');
    const apiUrl = `${this.SHOWCASES_API_BASE}?creative_id=${this.creativeId}`;

    return new Promise((resolve) => {
      this.http.get<any>(apiUrl, { headers }).subscribe(
        (response) => {
          console.log('Showcases API response:', response);
          console.log('Showcases API response (JSON):', JSON.stringify(response, null, 2));
          this.statusFromShowcases = this.parseStatusFromShowcasesResponse(response);
          console.log('Parsed statusFromShowcases (location_id -> status):', this.statusFromShowcases);
          this.mergeStatusIntoLocations();
          this.applyFilteredLocationsPage();
          this.cdr.detectChanges();
          this.triggerFirstPageAfterLoad();
          resolve();
        },
        () => {
          this.applyFilteredLocationsPage();
          this.cdr.detectChanges();
          this.triggerFirstPageAfterLoad();
          resolve();
        }
      );
    });
  }

  /**
   * Parse showcases API response into a map: location_id -> status (for display in status column).
   * API returns { items: [...] } with each item having location_id and showcase_status.
   */
  private parseStatusFromShowcasesResponse(res: any): Record<string, string> {
    const map: Record<string, string> = {};
    const list = res?.items ?? res?.locations ?? res?.data?.locations ?? (Array.isArray(res) ? res : []);
    if (!Array.isArray(list)) {
      return map;
    }
    list.forEach((item: any) => {
      const locId = item?.location_id ?? item?.locationId ?? item?._id ?? item?.id;
      const status = item?.showcase_status ?? item?.status ?? (typeof item?.status === 'object' ? item?.status?.name : undefined);
      if (locId != null && status != null) {
        map[String(locId)] = String(status);
      }
    });
    return map;
  }

  /** Merge statusFromShowcases into each location in filteredLocationsList for the status column. */
  private mergeStatusIntoLocations(): void {
    this.filteredLocationsList.forEach((loc: any) => {
      const key = String(loc.location_id ?? loc.locationId ?? loc._id ?? '').trim();
      if (this.statusFromShowcases[key]) {
        loc.status = this.statusFromShowcases[key];
      }
    });
  }


  private getAllLocations(): void {
    console.log("in getAllLocations")
    const API_URL = this.ownerName ? 'get-question-coll' : 'get-localpost-detail-coll';
    this.socketService.sendRequest(API_URL, {
      offset: this.offset,
      limit: this.pageSize,
      'detailLevel': 3,
      'where': this.whereClaus
     })
    .then(res => {
      console.log("res from getAllLocations",res)
      if (res['collection']?.length == 0) {
        this.notifyService.error('There are no locations available');
        this.tableData = [];
        this.paginatorLength = 0;
      } else {
        this.tableData = res['collection'].map(d => {
          return {
            ...d,
            attrs: d.attrs === null ? null : JSON.parse(d.attrs),
          }
        });
        this.paginatorLength = res['totalEntries'];
      }
      this.logPaginatorLength('getAllLocations');
    },
    (err: Error) => {
      this.tableData = [];
      console.warn('Error loading Locations: %j', err);
    })
  }

  updateTableData(event) {
    this.offset = event.first;
    if (this.isAllLocations || this.locationIds.length > 0) {
      this.applyFilteredLocationsPage();
    } else {
      this.whereClaus = this.buildWhereClaus(event.globalFilter || '');
      this.getAllLocations();
    }
  }

  lazyLoadLocations(event) {
    console.log("in lazyLoadLocations")
    this.offset = event.first;
    this.pageSize = event.rows ?? this.paginatorRows;
    if (this.locationIds.length > 0) {
      if (this.filteredLocationsList.length > 0) {
        this.applyFilteredLocationsPage();
      } else {
        this.ensureFiltersLocationsLoaded();
      }
    } else if (this.isAllLocations) {
      if (this.filteredLocationsList.length > 0) {
        this.applyFilteredLocationsPage();
      } else {
        this.ensureFiltersLocationsLoaded();
      }
    } else {
      console.log("in else lazyLoadLocations")
      this.getAllLocations();
    }
  }

  openAddLocation() {
    // Placeholder for add location functionality
  }

  getStatusDisplay(location: any): string {
    // Handle status as object or code
    if (location._status) {
      if (typeof location._status === 'object' && location._status.name) {
        return location._status.name;
      } else if (typeof location._status === 'string') {
        // Use dbService to convert status code to name
        try {
          return this.dbService.LocStatusType.codeWithName(location._status);
        } catch (e) {
          return location._status;
        }
      }
    }
    if (location.status) {
      if (typeof location.status === 'object' && location.status.name) {
        return location.status.name;
      } else if (typeof location.status === 'string') {
        return location.status;
      }
    }
    return '-';
  }

  buildWhereClaus(search) {
    const tempFilterArr: string[] = [];
    const id = this.ownerName ? `bulkowner_id=${this.id}` : `post_id=${this.id}`;
    let whereClauseArray: Array<any> = ['-and', id];
    const pushQueriesToMainArray = () => {
      whereClauseArray.push(...tempFilterArr);
      tempFilterArr.length = 0;
    };
    if (search) {
      tempFilterArr.push(`l.storeCode::text ~* '\\y${search}'`);
      // tempFilterArr.push(`l.businessName::text ~* '\\y${search}'`);
      pushQueriesToMainArray();
    }
    return whereClauseArray;
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    if (this.dynamicDialogConfig?.data?.openAddLocation) {
      this.dialogService.open(GbpQaAddLocationComponent, {
        width: '50%',
      });
    }
  }
}
